import { useTranslation } from "@refinedev/core"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6"

import { HTMLAttributes, useEffect, useMemo, useState } from "react";
import { createDaysForCurrentMonth, createDaysForNextMonth, createDaysForPreviousMonth } from "@utils/time";
import dayjs from "dayjs";


export interface CalendarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onDateChange'> {
    selectedDate?: dayjs.Dayjs | Date | string
    currentDate?: dayjs.Dayjs | Date | string
    onDateChange?: (value: string) => void
    onPageChange?: (value: any) => void
    selectedDates?: (dayjs.Dayjs | Date | string)[]
}


export const Calendar = ({ selectedDate: selected, currentDate, onDateChange, onPageChange, selectedDates, ...rest }: CalendarProps) => {
    const { translate } = useTranslation()
    const [_selectedDate, setSelectedDate] = useState(dayjs(selected) ?? dayjs().startOf('day'))

    useEffect(() => {
        if (selected && !dayjs(selected).isSame(_selectedDate, 'day')) {
            setSelectedDate(dayjs(selected))
        }
        if (!selected) {
            onDateChange?.(_selectedDate.format('YYYY-MM-DD'))
        }
    }, [selected])

    useEffect(() => {
        onPageChange?.(_selectedDate.format('YYYY-MM-DD'))
    }, [_selectedDate])

    const _currentDate = dayjs(currentDate).format("YYYY-MM-DD")
    const originalDate = dayjs(selected).format("YYYY-MM-DD")
    const year = _selectedDate.format("YYYY");
    const month = _selectedDate.format("M");
    const fdate = _selectedDate.format("YYYY-MM-DD")
    const selectedDatesSet = useMemo(() => new Set(selectedDates?.map(d => dayjs(d).format("YYYY-MM-DD")) ?? []), [selectedDates])

    const currentMonthDays = createDaysForCurrentMonth(year, month);
    const previousMonthDays = createDaysForPreviousMonth(currentMonthDays, year, month);
    const nextMonthDays = createDaysForNextMonth(currentMonthDays, year, month);

    const days = [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];

    return (
        <div className="w-full max-w-sm mx-auto" {...rest}>
            {/* Header with month/year and navigation */}
            <div className="flex items-center justify-between mb-6 px-2">
                <h2 className="text-lg md:text-xl font-semibold capitalize text-seco">
                    {`${_selectedDate.format("MMMM")} ${year}`}
                </h2>
                <div className="flex items-center gap-2">
                    <button
                        className="p-2 rounded-full hover:bg-secondary/10 active:bg-secondary/20 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        onClick={() => setSelectedDate(_selectedDate.subtract(1, 'month'))}
                        aria-label="Previous month"
                    >
                        <FaChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                        className="p-2 rounded-full hover:bg-secondary/10 active:bg-secondary/20 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        onClick={() => setSelectedDate(_selectedDate.add(1, 'month'))}
                        aria-label="Next month"
                    >
                        <FaChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
            </div>

            {/* Calendar grid */}
            <div className="w-full">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {[...Array(7).keys()].map((d, i) => (
                        <div
                            key={i}
                            className="h-8 flex items-center justify-center text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wide"
                        >
                            {translate(`common.daysWeekShorter.${(d + 1) % 7}`)}
                        </div>
                    ))}
                </div>

                {/* Calendar days */}
                <div className={`grid grid-cols-7 gap-1 ${days.length > 42 ? 'grid-rows-7' : 'grid-rows-6'}`}>
                    {days.map((d, i) => {
                        const showCurrentDate = _currentDate == d.date

                        let cellState = 'normal'
                        if (selectedDatesSet.has(d.date)) {
                            cellState = 'available'
                        }
                        if (originalDate == fdate && fdate == d.date) {
                            cellState = 'selected'
                        }
                        if (!d.isCurrentMonth) {
                            cellState = 'not-current-month'
                        }

                        return (
                            <button
                                key={i}
                                className={`
                                    relative w-full aspect-square min-h-[40px] md:min-h-[44px] 
                                    flex items-center justify-center rounded-full
                                    text-sm md:text-base font-medium
                                    transition-all duration-200 ease-in-out
                                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                                    active:scale-95
                                    ${cellState == 'normal' && 'text-gray-900 hover:bg-secondary/10 active:bg-secondary/20'} 
                                    ${cellState == 'available' && 'bg-primary shadow-md hover:bg-primary/80'} 
                                    ${cellState == 'selected' && 'bg-secondary text-white shadow-md hover:bg-secondary/70'} 
                                    ${cellState == 'not-current-month' && 'opacity-30'}
                                `}
                                onClick={() => onDateChange?.(d.date)}
                                disabled={cellState == 'not-current-month'}
                                aria-label={`${d.dayOfMonth} ${_selectedDate.format("MMMM")} ${year}`}
                            >
                                {d.dayOfMonth}
                                {showCurrentDate && (
                                    <span
                                        className={`
                                            absolute bottom-1 w-1 h-1 rounded-full
                                             ${cellState == 'normal' && 'bg-secondary'}
                                             ${cellState == 'available' && 'bg-secondary'}
                                             ${cellState == 'selected' && 'bg-primary'}
                                             ${cellState == 'not-current-month' && 'bg-secondary'}
                                        `}
                                    />
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}